import { z } from "zod";
import type { ExamConfig } from "./exam-config";
import {
  MIN_PUBLISHED_CARDS,
  MIN_PUBLISHED_QUESTIONS,
  applyVerdicts,
  assembleItems,
  assembleNotes,
  canPublish,
  chunkPages,
  itemsOutputSchema,
  notesOutputSchema,
  type CardDraft,
  type CaseDraft,
  type GuideSectionDraft,
  type MindMapNode,
  type QuestionDraft,
} from "./generation";

/**
 * StudyWard Core library (owner decision OD-1, plan §5.2a, BE10): NCLEX sets StudyWard generates once from openly
 * licensed nursing textbooks, owned by a system account and readable by every student who opts in. Turning it off
 * hides them; reset clears only the student's Core review schedule. Their own sets are never touched.
 */
export const CORE_LIBRARY_OWNER_ID = "c0de0000-0000-4000-8000-000000000001";

export const SET_ORIGINS = ["user", "core"] as const;
export const setOriginSchema = z.enum(SET_ORIGINS);
export type SetOrigin = z.infer<typeof setOriginSchema>;

/** GET /core-library */
export const coreLibraryStatusSchema = z.object({
  /** At least one published Core set exists for the student's exam. */
  available: z.boolean(),
  /** The Starter Kit is installed for this student (same as `enabled`; "install" = enable, "remove" = disable + delete Core progress). */
  installed: z.boolean(),
  enabled: z.boolean(),
  reset_at: z.string().nullable(),
  sets: z.number().int().nonnegative(),
  questions: z.number().int().nonnegative(),
  cards: z.number().int().nonnegative(),
});
export type CoreLibraryStatus = z.infer<typeof coreLibraryStatusSchema>;

/** PUT /core-library */
export const coreLibraryUpdateSchema = z.object({ enabled: z.boolean() });

/** POST /core-library/reset */
export const coreLibraryResetResponseSchema = z.object({ cleared_cards: z.number().int().nonnegative(), reset_at: z.string() });

/** POST /core-library/remove — uninstall the Starter Kit: disables it and deletes only this student's Core review state and answers. */
export const coreLibraryRemoveResponseSchema = coreLibraryStatusSchema;

// ---------------------------------------------------------------- ingestion (owner, source-agnostic — plan BE11)
/** Stored format of an ingested Core source: named sections, so citations read "Book title, 12.3" and not "¶4". */
export const CORE_SECTIONS_MIME = "application/vnd.studyward.core-sections+json";
export const coreSectionSchema = z.object({
  /** Citation anchor shown to students, e.g. "Pharmacology for Nurses, 12.3 Anticoagulants". */
  anchor: z.string().trim().min(1).max(80),
  text: z.string().trim().min(50).max(20_000),
});
export const coreSectionsSchema = z.array(coreSectionSchema).min(1).max(300);

/** Where the material comes from and under which licence — required for every Core set, shown to students. */
export const coreAttributionSchema = z.object({
  source_title: z.string().trim().min(1).max(200),
  authors: z.string().trim().max(300).default(""),
  /** The licence that allows StudyWard's commercial use, e.g. "Commercial licence from Rice University, 2026". */
  licence: z.string().trim().min(3).max(200),
  licence_url: z.string().trim().url().max(500).nullable().default(null),
  source_url: z.string().trim().url().max(500).nullable().default(null),
  /** The "changes were made" note CC BY requires; shown with the attribution. */
  changes: z.string().trim().min(1).max(300).default("Questions, flashcards and study notes were written from this text; wording was changed."),
});
export type CoreAttribution = z.infer<typeof coreAttributionSchema>;

/** POST /admin/core-library/sets — one Core set from one licensed source, generated like any other set. */
export const coreIngestRequestSchema = z
  .object({
    idempotency_key: z.string().min(8).max(80),
    title: z.string().trim().min(1).max(120),
    exam: z.enum(["nclex_rn", "nclex_pn"]),
    attribution: coreAttributionSchema,
    sections: coreSectionsSchema,
  })
  .refine((b) => b.sections.reduce((n, s) => n + s.text.length, 0) <= 500_000, { message: "Keep one Core set under 500,000 characters — split the source.", path: ["sections"] });
export type CoreIngestRequest = z.infer<typeof coreIngestRequestSchema>;

/** Admin list of Core sets with where each one stands on its way to students. */
export const adminCoreSetSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  exam: z.string(),
  status: z.string(),
  attribution: coreAttributionSchema,
  questions: z.number().int(),
  cards: z.number().int(),
  reviews: z.object({ pending: z.number().int(), approved: z.number().int(), quarantined: z.number().int(), unqueued: z.number().int() }),
  released_at: z.string().nullable(),
  /** Bumped on every title or attribution edit. */
  version: z.number().int().min(1),
  /** Items released by the owner's auto-approve decision that no human has reviewed yet. */
  auto_approved: z.number().int().nonnegative(),
  /** Ready, every published item approved, not yet released. */
  can_release: z.boolean(),
  created_at: z.string(),
});
export type AdminCoreSet = z.infer<typeof adminCoreSetSchema>;
export const adminCoreSetListSchema = z.object({ items: z.array(adminCoreSetSchema) });


// ---------------------------------------------------------------- admin edit / delete
/** PATCH /admin/core-library/sets/:id — title and/or attribution; bumps the set's version. */
export const coreSetUpdateSchema = z
  .object({ title: z.string().trim().min(1).max(120).optional(), attribution: coreAttributionSchema.optional() })
  .refine((b) => b.title !== undefined || b.attribution !== undefined, { message: "Change the title or the attribution." });
export type CoreSetUpdate = z.infer<typeof coreSetUpdateSchema>;
export const coreSetUpdateResponseSchema = z.object({ ok: z.literal(true), version: z.number().int().min(1) });

/** DELETE /admin/core-library/sets/:id and DELETE /admin/core-library/kit?exam= */
export const coreDeleteResponseSchema = z.object({ ok: z.literal(true), deleted_sets: z.number().int().nonnegative() });
export const coreKitQuerySchema = z.object({ exam: z.enum(["nclex_rn", "nclex_pn"]) });

// ---------------------------------------------------------------- import of ready-made sets (owner only, README §12.4)
export const CORE_AUTO_APPROVE_NOTE = "auto-approved by owner decision, pending later human review";

/** A verifier agent's verdict for one item ref ("q1", "f3"). Items without a "pass" are stored quarantined. */
export const coreImportVerdictSchema = z.object({ ref: z.string().min(1).max(40), verdict: z.enum(["pass", "regenerate", "quarantine"]), reason: z.string().max(500).default("") });

/**
 * POST /admin/core-library/sets/import — the set arrives finished: named sections (re-chunked here), items and cards
 * as the writer produced them (cited as c1, c2 … in chunk order), notes, and the verifier's verdicts. The route
 * revalidates all of it with the same schemas as generation and never calls an AI provider.
 */
export const coreImportRequestSchema = z
  .object({
    idempotency_key: z.string().min(8).max(80),
    title: z.string().trim().min(1).max(120),
    exam: z.enum(["nclex_rn", "nclex_pn"]),
    attribution: coreAttributionSchema,
    sections: coreSectionsSchema,
    items: z.object({ questions: z.array(z.unknown()).max(400), cards: z.array(z.unknown()).max(600), cases: z.array(z.unknown()).max(20).optional() }),
    notes: z.unknown(),
    verdicts: z.array(coreImportVerdictSchema).max(1100),
    /** Owner-only: mark every item approved in the clinical review queue and release the set (audited; items stay in QA sampling). */
    auto_approve: z.boolean().default(false),
  })
  .refine((b) => b.sections.reduce((n, s) => n + s.text.length, 0) <= 500_000, { message: "Keep one Core set under 500,000 characters — split the source.", path: ["sections"] });
export type CoreImportRequest = z.infer<typeof coreImportRequestSchema>;

export const coreImportResponseSchema = z.object({
  set_id: z.string().uuid(),
  questions: z.number().int().nonnegative(),
  cards: z.number().int().nonnegative(),
  quarantined: z.number().int().nonnegative(),
  /** True when auto_approve released it to students. Otherwise it is ready, queued for review, and hidden. */
  released: z.boolean(),
  auto_approved: z.boolean(),
  reused: z.boolean(),
});
export type CoreImportResponse = z.infer<typeof coreImportResponseSchema>;

export interface CoreImportPlan {
  chunks: Array<{ id: string; ord: number; anchor: string; text: string }>;
  cases: Array<CaseDraft & { status: "active" | "quarantined" }>;
  questions: Array<QuestionDraft & { status: "active" | "quarantined"; ord: number }>;
  cards: Array<CardDraft & { status: "active" | "quarantined"; ord: number }>;
  guide: Array<GuideSectionDraft & { ord: number }>;
  mind_map: MindMapNode;
  counts: { questions: number; cards: number; quarantined: number };
}

export type CoreImportResult = { ok: true; plan: CoreImportPlan } | { ok: false; message: string };

const fail = (message: string): CoreImportResult => ({ ok: false, message });

/**
 * Revalidates an import exactly like generation does (pure, no AI): chunks the sections, parses items and notes with
 * the exam's own schemas, rejects anything uncited, mis-tagged or off-format (strictly: one bad item fails the
 * import), applies the verdicts, keeps NGN cases whole, and enforces the publish rules (≥ 5 verified questions and
 * ≥ 5 cards).
 */
export function prepareCoreImport(
  body: CoreImportRequest,
  ctx: { config: Pick<ExamConfig, "blueprint" | "format_pack">; versions: { style_pack: string; format_pack: string; rules: string }; newId: () => string },
): CoreImportResult {
  const chunks = chunkPages(body.sections.map((s) => ({ anchor: s.anchor, text: s.text }))).map((c, ord) => ({ id: ctx.newId(), ord, anchor: c.anchor, text: c.text }));
  if (chunks.length === 0) return fail("The sections contain no text.");
  const chunkIds = chunks.map((c) => c.id);

  const items = itemsOutputSchema(ctx.config).safeParse(body.items);
  if (!items.success) return fail(`Items don't match the ${ctx.config.format_pack.version} format: ${items.error.issues.slice(0, 3).map((i) => `${i.path.join(".")} ${i.message}`).join("; ")}`);
  const notes = notesOutputSchema.safeParse(body.notes);
  if (!notes.success) return fail(`Notes are invalid: ${notes.error.issues.slice(0, 3).map((i) => `${i.path.join(".")} ${i.message}`).join("; ")}`);

  const refs = [...items.data.questions.map((q) => q.ref), ...items.data.cards.map((c) => c.ref)];
  if (new Set(refs).size !== refs.length) return fail("Every question and card needs its own ref.");
  const verdictRefs = body.verdicts.map((v) => v.ref);
  if (new Set(verdictRefs).size !== verdictRefs.length) return fail("Each ref may have only one verdict.");
  const unknown = verdictRefs.find((r) => !refs.includes(r));
  if (unknown) return fail(`A verdict names "${unknown}", which is not an item.`);

  const assembled = assembleItems(items.data, { config: ctx.config, chunkIds, versions: ctx.versions, newId: ctx.newId });
  if (assembled.rejected > 0) return fail(`${assembled.rejected} item(s) failed validation (uncited, wrong blueprint tags, wrong item type, or an incomplete case study).`);

  const outcome = applyVerdicts(refs, body.verdicts, 2);
  const statusOf = (ref: string): "active" | "quarantined" => (outcome.get(ref) === "active" ? "active" : "quarantined");
  const qRefs = items.data.questions.map((q) => q.ref);
  const cRefs = items.data.cards.map((c) => c.ref);
  const questions = assembled.questions.map((q, i) => ({ ...q, status: statusOf(qRefs[i]!), ord: i }));
  // A case study is published only whole: if any of its six items is not active, all of them are held back.
  const cases = assembled.cases.map((k) => {
    const whole = questions.filter((q) => q.case_id === k.id).every((q) => q.status === "active");
    if (!whole) for (const q of questions) if (q.case_id === k.id) q.status = "quarantined";
    return { ...k, status: whole ? ("active" as const) : ("quarantined" as const) };
  });
  const cards = assembled.cards.map((c, i) => ({ ...c, status: statusOf(cRefs[i]!), ord: i }));

  const active = { questions: questions.filter((q) => q.status === "active").length, cards: cards.filter((c) => c.status === "active").length };
  if (!canPublish(active)) return fail(`A Core set needs at least ${MIN_PUBLISHED_QUESTIONS} verified questions and ${MIN_PUBLISHED_CARDS} verified cards; this one has ${active.questions} and ${active.cards}.`);

  const note = assembleNotes(notes.data, chunkIds);
  if (note.guide.length === 0) return fail("The study guide needs at least one cited section.");
  return {
    ok: true,
    plan: {
      chunks,
      cases,
      questions,
      cards,
      guide: note.guide.map((g, ord) => ({ ...g, ord })),
      mind_map: note.mindMap,
      counts: { questions: active.questions, cards: active.cards, quarantined: questions.length + cards.length - active.questions - active.cards },
    },
  };
}
